import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Calendar } from 'primereact/calendar';
import './lectures-side-bar.css';
import { getHebrewYear } from '../../../utils/hebrew-date-utils';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import globalConfigStore from '../../../stores/global-config-store';
import { CustomExpander } from '../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { FilterCheckBoxItem } from '../../../components/CustomExpander/componenets/filter-check-box-item';
import globalSettingsStores from '../../../stores/settings-stores';
import { Button } from 'primereact/button';
import { AbstractProgram } from '../../../api/types/settings/studies/abstract-program';
import { LecturesSideBarFilterDatamodel } from './lectures-side-bar-filter-datamodel';
import {
  LectureStatus,
  getLectureStatusDisplayText,
} from '../../../api/types/programs/course/lectures/lecture-status.type';
import { CourseStatus } from '../../../api/types/programs/course/course-status';
import { toast } from 'react-toastify';
import { UserSummaryType } from '../../../api/types/user/user-summary.type';
import { AddProfessorsDialog } from './dialogs/add-professors-dialog';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface LecturesSideBarProps {
  onSearch: (data: LecturesSideBarFilterDatamodel) => void;
  loading: boolean;
}

export const LecturesSideBar = observer(
  ({ loading, onSearch }: LecturesSideBarProps) => {
    const currentYear = new Date().getFullYear();
    const globalYear = globalConfigStore.rootFilter.currentYear;
    const defaultYear = globalYear ?? currentYear;

    const [selectedBranchesIds, setSelectedBranchesIds] = useState<number[]>(
      [],
    );
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedStartYears, setSelectedStartYears] = useState<number[]>([
      defaultYear,
    ]);

    // Sync selectedStartYear when globalConfigStore.rootFilter.currentYear changes
    useEffect(() => {
      if (globalYear !== undefined) {
        setSelectedStartYears([globalYear]);
      }
    }, [globalYear]);
    const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
    const [selectedLectureStates, setSelectedLectureStates] = useState<
      LectureStatus[]
    >([]);
    const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
    const [selectedCourseStatuses, setSelectedCourseStatuses] = useState<
      CourseStatus[]
    >([]);
    const [planDateRange, setPlanDateRange] = useState<
      [Date | null, Date | null]
    >([null, null]);
    const [selectedProfessors, setSelectedProfessors] = useState<
      UserSummaryType[]
    >([]);
    const [showAddProfessorsDialog, setShowAddProfessorsDialog] =
      useState(false);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    // Get all classes from all branches
    const getAllClasses = () => {
      const classes: { name: string; id: number }[] = [];
      globalSettingsBranches.branches?.forEach((branch) => {
        branch.buildings?.forEach((building) => {
          building.classes?.forEach((classItem) => {
            classes.push({
              name: `${branch.branchName} - ${building.buildingName} - ${classItem.className}`,
              id: classItem.id,
            });
          });
        });
      });
      return classes;
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
        paddingRight: '4px',
      },
      dropdownContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      dropdownLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      dateRangeContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
    };

    const handleSearch = () => {
      if (!planDateRange[0]) {
        toast.warn('יש לבחור תאריך התחלה');
        return;
      }

      onSearch({
        branchesIds: selectedBranchesIds,
        programs: selectedPrograms,
        startYears: selectedStartYears,
        classes: selectedClasses,
        planDateRangeStart: planDateRange[0],
        planDateRangeEnd: planDateRange[1],
        lectureState: selectedLectureStates,
        courses: selectedCourses,
        courseStatuses: selectedCourseStatuses,
        professorIds: selectedProfessors.map((p) => p.id),
      });
    };

    const handleClearAllFilters = () => {
      setSelectedBranchesIds([]);
      setSelectedPrograms([]);
      setSelectedStartYears([]);
      setSelectedClasses([]);
      setSelectedLectureStates([]);
      setSelectedCourses([]);
      setSelectedCourseStatuses([]);
      setPlanDateRange([null, null]);
      setSelectedProfessors([]);
    };

    const handleRemoveProfessor = (professorId: number) => {
      setSelectedProfessors((prev) => prev.filter((p) => p.id !== professorId));
    };

    const handleProfessorsApproved = (professors: UserSummaryType[]) => {
      setSelectedProfessors(professors);
    };

    const isSearchDisabled = !planDateRange[0];
    const searchTooltip = isSearchDisabled
      ? 'נדרש לבחור תאריך התחלה'
      : 'לחץ לחיפוש';

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחה"
            items={globalSettingsBranches.branches ?? []}
            selectedItems={selectedBranchesIds}
            onItemToggle={(branchId) => {
              setSelectedBranchesIds((prev) =>
                prev.includes(branchId)
                  ? prev.filter((b) => b !== branchId)
                  : [...prev, branchId],
              );
            }}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            searchPlaceholder="חפש שלוחה..."
          />

          <CustomExpander badge={selectedProfessors.length}>
            <div>מרצים</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '4px 0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
              >
                <Button
                  icon="pi pi-plus"
                  tooltip="הוסף מרצה"
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => setShowAddProfessorsDialog(true)}
                  style={{
                    height: '30px',
                    width: '30px',
                  }}
                />
              </div>
              {selectedProfessors.length === 0 ? (
                <div
                  style={{
                    color: '#666',
                    fontSize: '0.9rem',
                    textAlign: 'right',
                  }}
                >
                  לא נבחרו מרצים
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    borderRadius: '6px',
                    padding: '4px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                  }}
                >
                  {selectedProfessors.map((professor) => (
                    <FilterCheckBoxItem
                      key={professor.id.toString()}
                      label={`${professor.firstName} ${professor.lastName}`}
                      isChecked={true}
                      onChange={() => handleRemoveProfessor(professor.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="שם הקורס"
            items={globalSettingsStores.abstractCourses}
            selectedItems={selectedCourses}
            onItemToggle={(courseId) => {
              setSelectedCourses((prev) =>
                prev.includes(courseId)
                  ? prev.filter((c) => c !== courseId)
                  : [...prev, courseId],
              );
            }}
            getItemLabel={(course) => course.courseName}
            getItemKey={(course) => course.id}
            searchPlaceholder="חפש קורס..."
          />

          <FilterExpanderWithListOfCheckboxes<AbstractProgram, number>
            title="תכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={(programId) => {
              setSelectedPrograms((prev) =>
                prev.includes(programId)
                  ? prev.filter((p) => p !== programId)
                  : [...prev, programId],
              );
            }}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes<YearOption, number>
            title="שנת התחלה של התוכנית"
            items={years}
            selectedItems={selectedStartYears}
            onItemToggle={(year) => {
              setSelectedStartYears((prev) =>
                prev.includes(year)
                  ? prev.filter((y) => y !== year)
                  : [...prev, year],
              );
            }}
            getItemLabel={(y) => y.year}
            getItemKey={(y) => y.value}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander>
            <div>טווח תאריכים</div>
            <div style={styles.dateRangeContainer}>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>מתאריך:</span>
                <Calendar
                  value={planDateRange[0]}
                  onChange={(e) =>
                    setPlanDateRange([e.value as Date, planDateRange[1]])
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>עד תאריך:</span>
                <Calendar
                  value={planDateRange[1]}
                  onChange={(e) =>
                    setPlanDateRange([planDateRange[0], e.value as Date])
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  minDate={planDateRange[0] || undefined}
                />
              </div>
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes<
            { name: string; id: number },
            number
          >
            title="כיתות"
            items={getAllClasses()}
            selectedItems={selectedClasses}
            onItemToggle={(classId) => {
              setSelectedClasses((prev) =>
                prev.includes(classId)
                  ? prev.filter((c) => c !== classId)
                  : [...prev, classId],
              );
            }}
            getItemLabel={(classItem) => classItem.name}
            getItemKey={(classItem) => classItem.id}
            searchPlaceholder="חפש כיתה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס קורס"
            items={Object.values(CourseStatus).filter(
              (value) => typeof value === 'string',
            )}
            selectedItems={selectedCourseStatuses}
            onItemToggle={(status) => {
              setSelectedCourseStatuses((prev) =>
                prev.includes(status as CourseStatus)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status as CourseStatus],
              );
            }}
            getItemLabel={(status) => {
              return CourseStatus.getDisplayText(status as CourseStatus);
            }}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס מפגש"
            items={Object.values(LectureStatus).filter(
              (value) => typeof value === 'string',
            )}
            selectedItems={selectedLectureStates}
            onItemToggle={(status) => {
              setSelectedLectureStates((prev) =>
                prev.includes(status as LectureStatus)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status as LectureStatus],
              );
            }}
            getItemLabel={(status) => {
              return getLectureStatusDisplayText(status as LectureStatus);
            }}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />
        </div>
        <Button
          severity="success"
          onClick={handleSearch}
          style={{ width: '100%', marginTop: '16px' }}
          loading={loading}
          disabled={isSearchDisabled}
          tooltip={searchTooltip}
          tooltipOptions={{ position: 'top', showOnDisabled: true }}
        >
          חפש
        </Button>

        <AddProfessorsDialog
          visible={showAddProfessorsDialog}
          alreadySelectedProfessors={selectedProfessors}
          onHide={() => setShowAddProfessorsDialog(false)}
          onApprove={handleProfessorsApproved}
        />
      </div>
    );
  },
);
